import { type FormikProps } from "formik"
import React from "react"

import { GiftCardValidityResponseType } from "./GiftCardType"
import {
  CustomerMembershipResponseType,
  GetOrderDetailsResponseType,
  OrderStatusTypes,
  ProductMembershipType,
  ProductType,
} from "./OrderTypes"
import { ProductAddonType } from "./ProductTypes"
import { IDomain, MetaDataType, PaginationLinkType } from "./UserTypes"

export interface CustomerType {
  id: string
  first_name: string
  last_name: string
  email: string
  phone: string
  phone_code: string
  address: string
  country: string
  city: string
  state: string
  zip_code: string
  status: number
  created_at: string
  updated_at: string
  address1: string
  address2: string
  language_iso2: string
  profile_image: string
  is_member: number
  member_code: string
  phone_verified_at: string | null
  email_verified_at: string
}

export interface AddOrEditCustomersModalType {
  profile_image?: File
  language_iso2?: string
  first_name: string
  last_name: string
  email: string
  phone: string
  phone_code: string
  address1: string
  address2: string
  country: string
  city: string
  state: string
  zip_code: string
  status?: string
}
export interface AddOrEditCustomerInfoModalType {
  show: boolean
  isEdit: boolean
  id?: string
  handleCloseDrawer: () => void
  onSuccess?: () => void
  formikRef?: React.RefObject<FormikProps<AddOrEditCustomersModalType>>
  optionalEmail?: boolean
}

export interface CreateCustomerRequestType {
  first_name: string
  last_name: string
  email: string
  phone: string
  phone_code: string
  address1: string
  address2: string
  country: string
  city: string
  state: string
  zip_code: string
  status: string
}

export interface Customer extends CreateCustomerRequestType {
  id: string
  created_at: string
  updated_at: string
}

export interface CreateCustomerResponseType {
  data: CustomerType
  [key: string]: any
}

export interface GetAllCustomerResponseType {
  data: CustomerType[]
  total: number
  limit: number
  skip: number
  [key: string]: any
}

export interface GetAllCustomerOrdersDataType {
  order_number: string
  id: string
  customer: {
    id: string
    creator_system_meta: object | null
    creator_system: string
    tenant_id: string
    domain_id: string
    created_by: string
    first_name: string
    last_name: string
    email: string
    phone: string
    phone_code: string
    address1: string
    address2: string
    city: string
    state: string
    country: string
    zip_code: string
    status: string
    created_at: string
    updated_at: string
    deleted_at: string | null
    receive_marketing_emails: boolean
    receive_sms_updates: boolean
    full_name: string
    is_member: number
    is_phone_verified: number
    is_email_verified: number
  }
  customerMembership?: CustomerMembershipResponseType["customerMembership"]
  status: OrderStatusTypes
  total_formatted: string
  total_due_formatted: string
  total_paid_formatted: string
  channel: string
  created_by: string
  creator_type: string
  created_at: string
  items: {
    id: string
    product: {
      id: string
      created_at: string
      updated_at: string
      deleted_at: string | null
      name: string
      code: string
      product_type_id: string
      description: string
      visibility: string
      terms_and_conditions: string
      cancellation_policy: string | null
      widget_description: string | null
      status: string
      meta: {
        merchandise?: ProductAddonType["merchandise"]
        gift_card: GiftCardValidityResponseType["data"] | null
        membership?: ProductMembershipType | null
      } | null
      tenant_id: string
      created_by: string
      domain_id: string
      advertise_price: string | null
      minimum_participants: number
      minimum_participants_booking_display_message: string | null
      merchandise_sell_individual: number
      merchandise_sell_addon: number
      has_add_on: number
      type: {
        id: string
        created_at: string
        updated_at: string
        deleted_at: string | null
        name: string
        internal_type: string
        description: string
        visibility: string
      }
      domain: IDomain
    }
  }[]
  currency: string
}

export interface GetAllCustomerOrdersResponseType {
  data: GetAllCustomerOrdersDataType[]
  links: PaginationLinkType
  meta: MetaDataType
}

export interface GetAllCustomerBookingsDataType {
  id: string
  product_id: string
  product_name: string
  is_dynamic_rental: boolean
  category: string
  order: {
    id: string
    customer_id: string
    tenant_id: string
    domain_id: string
    created_by: string | null
    sub_total: number
    offer_total: number
    levies_total: number
    total: number
    currency: string
    special_requirement: string | null
    placed_at: string | null
    status: OrderStatusTypes
    order_number: string
    payment_status: string
    creator_system_meta: []
    creator_system: string
    affiliate_id: string | null
    offer_id: string | null
    created_at: string
    updated_at: string
    deleted_at: string | null
    tip: number
    channel: string
    platform: string
    creator_type: string
    creator_id: string
    offer_code: string | null
    crew_count: number
    customer_membership_id: string | null
    member_id: string | null
    laravel_through_key: string
    total_refunded: number
    formatted_total_refunded: string
    total_due: string
    formatted_total_due: string
    total_paid: number
    formatted_total_paid: string
    balance: number
    formatted_balance: string
    formatted_total: string
    application_fees: number
    application_fees_paid: number
    application_fees_balance: number
    refunds: []
    transactions: []
    levies: {
      id: string
      created_at: string
      updated_at: string
      order_item_id: string
      levy_id: string
      name: string
      value: string
      is_price_inclusive: boolean
      is_compounded: boolean
      amount: number
      currency: string
      category_name: string
      type_name: string
      is_hidden: boolean
      laravel_through_key: string
    }[]
    customer: GetAllCustomerOrdersDataType["customer"]
    tips: GetOrderDetailsResponseType["tips"]
    offer: null
  }
  slot: {
    id: string
    created_at: string
    updated_at: string
    product_id: string
    tenant_id: string
    domain_id: string
    assets: null
    product_availability_id: string
    start_at: string
    end_at: string
    cancelled_at: string | null
    available: number
    booked: number
    hold: number
    is_real_time_created: number
    deleted_at: string | null
    crew_count: number
    local_start_time: string
    local_end_time: string
    availability: object
    product: {
      id: string
      name: string
      product_type_id: string
      has_add_on: number
      type: {
        id: string
        name: string
        internal_type: string
      }
      domain: IDomain
    }
    bookings: {
      id: string
      domain_id: string
      tenant_id: string
      order_item_id: string
      product_id: string
      checked_in: number
      cancelled_at: string | null
      cancellation_reason: string | null
      check_in_date: string | null
      check_out_date: string | null
      slot_id: string
      status: OrderStatusTypes
      created_at: string
      updated_at: string
      cart_item_id: string
      booking_number: string
      slot_uuid: string
      crew_count: number
      deleted_at: string | null
    }[]
  }
  customer: GetAllCustomerOrdersDataType["customer"]
  checked_in: number
  cancelled_at: string | null
  cancellation_reason: string | null
  check_in_date: string | null
  check_out_date: string | null
  slot_id: string
  status: OrderStatusTypes
  created_at: string
  updated_at: string
  booking_number: string
  crew: {
    attachable_type: string
    attachable_id: string
    count: number
  }
  order_commission: null
  quantities: {
    id: string
    quantity: number
    unit: {
      id: string
      created_at: string
      updated_at: string
      deleted_at: string | null
      name: string
      internal_name: string | null
      description: string
      id_required: boolean
      capacity_used: number
      is_default: number
      tenant_id: string
      created_by: string
      domain_id: string
      product_id: string
      pricing_structure_type: string
      price_type: string
      min_quantity: number
      duration: number
      max_quantity: number | null
      minimum_duration: number
      maximum_duration: number
      is_multi_day: number
      has_flexible_timings: number
      visibility: string
      is_calculation_based_on_calendar_days: number
    }
    price_in_cents: number
    display_price: number
    formatted_price: string
    display_total: number
    formatted_total: string
  }[]
}

export interface GetAllCustomerBookingsResponseType {
  data: GetAllCustomerBookingsDataType[]
  links: PaginationLinkType
  meta: MetaDataType
}

export interface GetCustomerMembershipType {
  id: string
  start_at: string
  end_at: string
  product: ProductType
  code: string | null
  cancellation_date: string | null
  cancellation_reason: string | null
  is_active: number
  customer: CustomerType
  order: GetOrderDetailsResponseType
}

export interface GetCustomerMembershipsResponseType {
  data: GetCustomerMembershipType[]
  links: PaginationLinkType
  meta: MetaDataType
}

export interface ILoggedInCustomerDetails {
  id: string
  first_name: string
  last_name: string
  email: string
  phone: string | null
  phone_code: string | null
  address1: string | null
  address2: string | null
  city: string | null
  state: string | null
  country: string | null
  zip_code: string | null
  created_at: string
  updated_at: string
  status: string
  language_iso2: string | null
  profile_image: string | null
}

export interface SignupCustomerRequestType {
  email: string
  password: string
  first_name: string
  last_name: string
  password_confirmation: string
}

export interface SignupCustomerResponseType {
  token: {
    access_token: string
    token_type: string
    expires_at: string | null
  }
  customer: ILoggedInCustomerDetails
  tenant: {
    id: string
    phone: string | null
    company: string | null
    domain: IDomain
  }
}

export interface SigninCustomerWithPasswordRequestType {
  email: string
  password: string
}

export enum SendOtpFieldType {
  PHONE = "phone",
  MEMBER_CODE = "member_code",
  EMAIL = "email",
}

export interface SendOtpRequestType {
  field_type: SendOtpFieldType
  value?: string
  phone?: string
  phone_code?: string
  type: string
}

export interface SendOtpResponseType {
  attempts_left: number // reattempts left for user
  message: string
  cooldown_remaining_seconds: number // interval between each resends
  reattempt_remaining_seconds: number // interval that user has to wait if reattempts are exhausted
  expiry: number // OTP valid time in secounds
}

export interface VerifyOtpRequestType extends SendOtpRequestType {
  otp_code: string
}

export interface CustomerGoogleSigninResponseType {
  url: string
}

export interface CustomerLoginForgotPasswordRequestType {
  email: string
}

export interface CustomerLoginForgotPasswordResponseType {
  message: string
}

export interface CustomerLoginResetPasswordRequestType {
  email: string
  token: string
  password: string
  password_confirmation: string
}

export interface CustomerLoginResetPasswordResponseType {
  message: string
  status: string
}

export interface UpdateCustomerMembershipStatusRequestType {
  is_active: number
}

export interface UpdateCustomerMembershipStatusResponseType {
  model: {
    id: string
    created_at: string
    updated_at: string
    deleted_at: string | null
    tenant_id: string
    domain_id: string
    product_id: string
    start_at: string
    end_at: string
    code: string
    cancellation_date: string | null
    cancellation_reason: string | null
    is_active: number
    customer_id: string
    order_id: string
    order_item_id: string
  }
  message: string
}
export interface SaveCardInStripeRequestType {
  payment_token: string
  payment_method: string
  name_on_card: string
}

export interface SaveCardInStripeType {
  id: string
  name_on_card: string
  customer_id: string
  payment_token: string
  integration_id: string
  brand: string
  last4: string
  exp_month: number
  exp_year: number
  masked_card_number: string
  is_expired: boolean
  is_default: boolean | null
  metadata: {
    payment_method_id: string
    customer_id: string
    setup_intent_id: string
  }
  created_at: string
  updated_at: string
  deleted_at: string | null
  integration: {
    id: string
    created_at: string
    updated_at: string
    deleted_at: string | null
    tenant_id: string
    domain_id: string
    name: string
    type: string
    status: string
    parent_integration_id: string | null
  }
}

export interface SaveCardInStripeResponseType {
  message: string
  data: SaveCardInStripeType
}

export type GetSavedCardsOfCustomerResponseType = SaveCardInStripeType[]
